import { useAuth } from "@/contexts/AuthContext";
import { useAPI } from "@/hooks/useAPI";
import type { Physician } from "@/services/physiciansService.types";
import { PhysiciansServiceV2 } from "@/services/physiciansServiceV2";
import type {
  InvitePhysicianPayload,
  UpdatePhysicianPayload,
} from "@/services/physiciansServiceV2.types";
import { getErrorMessage } from "@/utils/errorUtils";
import { isAdmin } from "@/utils/roleUtils";
import { useCallback, useMemo } from "react";
import useSWR, { mutate } from "swr";

const PHYSICIANS_KEY = "/physicians";

export const usePhysicians = () => {
  const apiV2 = useAPI("v2");
  const { firmContext } = useAuth();
  const physiciansServiceV2 = useMemo(() => new PhysiciansServiceV2(apiV2), [apiV2]);
  const firmId = firmContext?.firmId;

  // Firm-scoped so a firm switch does not serve a stale roster.
  const cacheKey = firmId && isAdmin(firmContext) ? [PHYSICIANS_KEY, firmId] : null;

  const {
    data,
    error,
    isLoading,
    mutate: revalidate,
  } = useSWR<Physician[]>(
    cacheKey,
    () => {
      if (!firmId) throw new Error("Firm context is not available");
      return physiciansServiceV2.listPhysicians(firmId);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 1 * 24 * 60 * 60 * 1000,
      shouldRetryOnError: true,
      errorRetryCount: 2,
    }
  );

  const updatePhysicianName = useCallback(
    async (userId: string, name: string): Promise<void> => {
      if (!firmId) throw new Error("Firm context is not available");
      await physiciansServiceV2.updatePhysician(firmId, userId, { name });
      invalidatePhysiciansCache(firmId);
    },
    [physiciansServiceV2, firmId]
  );

  const updatePhysicianSpecialty = useCallback(
    async (userId: string, specialtyId: string): Promise<void> => {
      if (!firmId) throw new Error("Firm context is not available");
      const payload: UpdatePhysicianPayload = { specialty_id: specialtyId };
      await physiciansServiceV2.updatePhysician(firmId, userId, payload);
      invalidatePhysiciansCache(firmId);
    },
    [physiciansServiceV2, firmId]
  );

  const invitePhysician = useCallback(
    async (invite: InvitePhysicianPayload): Promise<void> => {
      if (!firmId) throw new Error("[usePhysicians] invitePhysician requires a firm context");
      await physiciansServiceV2.invitePhysician(firmId, invite);
      invalidatePhysiciansCache(firmId);
    },
    [physiciansServiceV2, firmId]
  );

  const reinvitePhysician = useCallback(
    async (email: string): Promise<string> => {
      if (!firmId) throw new Error("[usePhysicians] reinvitePhysician requires a firm context");
      const message = await physiciansServiceV2.reinvitePhysician(firmId, { email });
      invalidatePhysiciansCache(firmId);
      return message;
    },
    [physiciansServiceV2, firmId]
  );

  return {
    physicians: data || [],
    isLoading,
    isError: Boolean(error),
    errorMessage: getErrorMessage(error),
    refetch: () => revalidate(),
    updatePhysicianName,
    updatePhysicianSpecialty,
    invitePhysician,
    reinvitePhysician,
  };
};

// Single-argument mutate: passing `undefined` as a second arg would write it into the
// cache before refetching, blanking the roster mid-revalidation.
export const invalidatePhysiciansCache = (firmId: string | undefined): void => {
  if (!firmId) return;
  mutate((key: unknown) => Array.isArray(key) && key[0] === PHYSICIANS_KEY && key[1] === firmId);
};
